import { useEffect, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { ApiError } from '../api/client';
import {
  getCrmPlatformSuggestions,
  searchCrmClients,
  type CrmClientHit,
  listLeads,
  createLead,
  type LeadListItem,
  type LeadStatus,
  type LeadKind,
} from '../api/crm';
import { useAuth } from '../auth/AuthContext';
import { COUNTRY_DIAL_CODES, COMMON_DIAL_ISO, isoToFlag } from './country-codes';
import { useFeather } from '../shell/useFeather';
import { CrmLeadDetailPage } from './CrmLeadDetailPage';
import { CrmStats } from './CrmStats';
import { CrmKindStats } from './CrmKindStats';
import { dealValues, leadFlow } from './crm-flow';
import './crm-stats.css';
import { stageDef, stagesForSlug } from './crm-stages';
import { COMM_LANGS, INZERENT_SERVICES, INZERENT_TYPES, LANGS, inzerentServiceLabels, inzerentTypeLabel, langDef } from './crm-lang';
import '../affilmaster/affilmaster-detail.css';
import './crm-detail.css';
import './crm-redesign.css';
import { CZK_PER_EUR, czkFirst, money, moneyAlt, moneyUnit } from './crm-money';
import './crm-leads.css';
import { ArcPage, ArcSelect, ArcSummaryGrid, ArcSummaryMetric, ArcToolbar } from '../components/arc/primitives/arc-primitives';

export const FOCUS_OPTIONS = ['Online casino / sportsbet', 'Land-based casino', 'Obojí', 'Network'];
export const AP_PRODUCTS = ['iGaming Affiliate', 'Land Based Affiliate', 'Obojí'];
export const AP_MODULES = ['Standard', 'Pro', 'Pro + Multibrand', 'Pro + AI', 'Pro Full'];
const FOCUS_OPTIONS_AM = ['web', 'streamer (influencer)', 'mailing'];

function focusOptionsForSlug(slug: string | null | undefined): string[] {
  return slug === 'affilmaster' ? FOCUS_OPTIONS_AM : FOCUS_OPTIONS;
}
function showProductForSlug(slug: string | null | undefined): boolean {
  return slug !== 'affilmaster';
}

function PhonePrefixOptions() {
  const common = COMMON_DIAL_ISO.map((iso) => COUNTRY_DIAL_CODES.find((c) => c.iso === iso)).filter(Boolean) as typeof COUNTRY_DIAL_CODES;
  const rest = COUNTRY_DIAL_CODES.filter((c) => !COMMON_DIAL_ISO.includes(c.iso));
  return (
    <>
      <optgroup label="Časté">
        {common.map((c) => <option key={c.iso} value={c.dial}>{isoToFlag(c.iso)} {c.name} ({c.dial})</option>)}
      </optgroup>
      <optgroup label="Všechny země">
        {rest.map((c) => <option key={c.iso} value={c.dial}>{isoToFlag(c.iso)} {c.name} ({c.dial})</option>)}
      </optgroup>
    </>
  );
}

const intFmt = (n: number) => n.toLocaleString('cs-CZ');

function initials(first: string, last: string) {
  return ((first[0] ?? '') + (last[0] ?? '')).toUpperCase();
}
function fmtDate(s: string) {
  if (!s) return '-';
  const d = new Date(s);
  return Number.isNaN(d.getTime())
    ? '-'
    : d.toLocaleDateString('cs-CZ', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function StatusBadge({ status, spaceSlug }: { status: LeadStatus; spaceSlug?: string | null }) {
  const s = stageDef(spaceSlug, status);
  return (
    <span
      className="pill crm-stage-badge"
      style={{ '--crm-badge-color': s.color } as CSSProperties}
    >
      {s.label}
    </span>
  );
}

function leadFocusLabel(lead: Pick<LeadListItem, 'kind' | 'focus' | 'product'>, amInzerentType: boolean): string | null {
  return amInzerentType && lead.kind === 'inzerent' ? inzerentTypeLabel(lead.product) : (lead.focus || null);
}

function FlowMini({ lead }: { lead: LeadListItem }) {
  if (lead.status === 'rejected') {
    return (
      <span className="crm-flow-mini is-rejected" title="Lead neschválen">
        <span className="crm-flow-mini-bar" aria-hidden="true">{[0, 1, 2, 3, 4].map((i) => <i key={i} />)}</span>
        <small>Neschváleno</small>
      </span>
    );
  }
  const f = leadFlow(lead);
  const label = f.current === -1 ? 'Hotovo' : f.labels[f.current];
  return (
    <span className="crm-flow-mini" title={`Postup ${f.doneCount} z ${f.labels.length}: ${label}`}>
      <span className="crm-flow-mini-bar" aria-hidden="true">
        {f.done.map((d, i) => <i key={i} className={d ? 'is-done' : i === f.current ? 'is-current' : ''} />)}
      </span>
      <small>{f.doneCount}/{f.labels.length} · {label}</small>
    </span>
  );
}

function fmtLeadDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric' });
}

export function KindBadge({ kind }: { kind: LeadKind }) {
  const isInzerent = kind === 'inzerent';
  return (
    <span className={`pill crm-kind-badge ${isInzerent ? 'inzerent' : 'affiliate'}`}>
      {isInzerent ? 'Inzerent' : 'Affiliate'}
    </span>
  );
}

function LangBadge({ lang }: { lang: string | null }) {
  const d = langDef(lang);
  if (!d) return <span className="text-muted">-</span>;
  return (
    <span className="pill crm-lang-badge" title={d.fullName}>
      {d.flag} {d.label}
    </span>
  );
}

export function CrmLeadyPage({
  detailId,
  onOpenDetail,
  onCloseDetail,
  actionsSlotId,
  onOpenAffiliate,
  onOpenClient,
}: {
  detailId?: string;
  onOpenDetail: (id: string) => void;
  onCloseDetail: () => void;
  actionsSlotId?: string;
  onOpenAffiliate?: (papAffiliateId: string) => void;
  onOpenClient?: (name: string, partnerId: number) => void;
}) {
  const [actionsSlot, setActionsSlot] = useState<HTMLElement | null>(null);
  const view: 'list' | 'detail' | 'stats' | 'new' = detailId === 'statistiky' ? 'stats' : detailId === 'novy' ? 'new' : detailId ? 'detail' : 'list';
  useEffect(() => {
    setActionsSlot(actionsSlotId ? document.getElementById(actionsSlotId) : null);
  }, [actionsSlotId, view]);
  const { me } = useAuth();
  const spaceSlug = me?.activeSpaceSlug ?? null;
  const stages = stagesForSlug(spaceSlug);
  const showLangFilter = spaceSlug === 'affilmaster';
  const showKindFilter = spaceSlug === 'affilmaster';
  const defaultStatus = spaceSlug === 'affilplatform' ? 'open' : 'active';

  const [query, setQuery] = useState('');
  const [status, setStatus] = useState(defaultStatus);
  const [kind, setKind] = useState<'all' | LeadKind>('all');
  const [lang, setLang] = useState('all');
  const [service, setService] = useState('all');
  const [limit, setLimit] = useState(25);
  const [offset, setOffset] = useState(0);

  const [rows, setRows] = useState<LeadListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const activeCount = rows.filter((lead) => !['won', 'lost', 'rejected'].includes(lead.status)).length;
  const assignedCount = rows.filter((lead) => lead.assignedToName).length;
  const deals = dealValues(rows, spaceSlug);
  const dealMultiplier = czkFirst(spaceSlug) ? CZK_PER_EUR : 1;
  const dealSuffix = ` ${moneyUnit(spaceSlug)}`;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      listLeads({
        search: query.trim() || undefined,
        status: status !== 'all' ? (status as LeadStatus | 'active' | 'open') : undefined,
        kind: showKindFilter && kind !== 'all' ? kind : undefined,
        lang: showLangFilter && lang !== 'all' ? lang : undefined,
        service: showKindFilter && service !== 'all' ? service : undefined,
        limit,
        offset,
      })
        .then((res) => {
          if (cancelled) return;
          setRows(res.items);
          setTotal(res.total);
          setError(null);
        })
        .catch((e: unknown) => {
          if (cancelled) return;
          setError(e instanceof ApiError ? e.message : 'Načtení selhalo');
          setRows([]);
          setTotal(0);
        })
        .finally(() => !cancelled && setLoading(false));
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, status, kind, lang, service, limit, offset, reloadKey, showLangFilter, showKindFilter]);

  useEffect(() => {
    setOffset(0);
  }, [query, status, kind, lang, service, limit]);

  useFeather([rows, view, actionsSlot]);

  if (view === 'detail' && detailId) {
    return <CrmLeadDetailPage id={Number(detailId)} onBack={onCloseDetail} onChanged={() => setReloadKey((k) => k + 1)} onOpenAffiliate={onOpenAffiliate} onOpenClient={onOpenClient} />;
  }

  if (view === 'stats') {
    return spaceSlug === 'affilmaster'
      ? <CrmKindStats spaceSlug={spaceSlug} onClose={onCloseDetail} />
      : <CrmStats spaceSlug={spaceSlug} onClose={onCloseDetail} />;
  }

  function clearFilters() {
    setQuery('');
    setStatus(defaultStatus);
    setKind('all');
    setLang('all');
    setService('all');
    setLimit(25);
    setOffset(0);
  }

  const actions = (
    <div className="crm-head-actions">
      <div className="crm-head-actions-buttons">
        <button type="button" className="crm-stats-head-btn" onClick={() => onOpenDetail('statistiky')}>
          <span className="feather-icon"><i data-feather="bar-chart-2"></i></span> Statistiky
        </button>
        <button type="button" className="btn btn-primary crm-head-new-btn" onClick={() => onOpenDetail('novy')}><span aria-hidden="true">+</span> Nový lead</button>
      </div>
      <div className="crm-head-actions-count">
        {loading ? 'Načítám…' : `${intFmt(rows.length)} z ${intFmt(total)} leadů`}
      </div>
    </div>
  );

  return (
    <ArcPage as="div" className="crm-page crm-leads-page">
      {actionsSlotId ? (
        actionsSlot ? createPortal(actions, actionsSlot) : null
      ) : (
        <header className="prototype-page-head crm-leads-head">
          <div><div className="prototype-eyebrow">CRM</div><h1>Leady</h1><p>Kontakty, které čekají na další obchodní krok.</p></div>
          {actions}
        </header>
      )}

      <ArcSummaryGrid>
        <ArcSummaryMetric label="Aktivní příležitosti" value={activeCount} context="čekají na další krok" />
        <ArcSummaryMetric label="Předpoklad příjmů" value={Math.round(deals.table * dealMultiplier)} suffix={` ${dealSuffix}`} context={`na stole, za měsíc${deals.table && moneyAlt(deals.table, spaceSlug) ? ` · ${moneyAlt(deals.table, spaceSlug)}` : ''}`} />
        <ArcSummaryMetric label="Potvrzené příjmy" value={Math.round(deals.closed * dealMultiplier)} suffix={` ${dealSuffix}`} context={`za měsíc${deals.closed && moneyAlt(deals.closed, spaceSlug) ? ` · ${moneyAlt(deals.closed, spaceSlug)}` : ''}`} />
        <ArcSummaryMetric label="Přiřazeno" value={assignedCount} context="má svého vlastníka" />
      </ArcSummaryGrid>

      <ArcToolbar className="crm-leads-toolbar" label="Filtry leadů" end={(
        <label className="crm-leads-search">
          <i data-feather="search" aria-hidden="true" />
          <input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Hledat jméno, e-mail nebo telefon…" aria-label="Hledat lead" />
          {query ? <button type="button" onClick={() => setQuery('')} aria-label="Vymazat hledání">×</button> : null}
        </label>
      )}>
        <div className="crm-leads-filters">
          {showKindFilter ? (
            <div className="crm-leads-filter">
              <ArcSelect label="Typ leadu" value={kind} onChange={(value) => setKind(value as 'all' | LeadKind)} options={[{ value: 'all', label: 'Všechny typy' }, { value: 'affiliate', label: 'Affiliate' }, { value: 'inzerent', label: 'Inzerent' }]} />
            </div>
          ) : null}
          {showKindFilter ? (
            <div className="crm-leads-filter">
              <ArcSelect label="Produkt" value={service} onChange={setService} options={[{ value: 'all', label: 'Všechny produkty' }, ...INZERENT_SERVICES.map((item) => ({ value: item.value, label: item.label }))]} />
            </div>
          ) : null}
          <div className="crm-leads-filter">
            <ArcSelect label="Stav" value={status} onChange={setStatus} options={[...(spaceSlug === 'affilplatform' ? [{ value: 'open', label: 'Aktivní' }] : []), { value: 'active', label: 'Vše kromě neschválených' }, { value: 'all', label: 'Všechny stavy' }, ...stages.map((stage) => ({ value: stage.key, label: stage.label }))]} />
          </div>
          {showLangFilter ? (
            <div className="crm-leads-filter">
              <ArcSelect label="Jazyk" value={lang} onChange={setLang} options={[{ value: 'all', label: 'Všechny jazyky' }, ...LANGS.map((item) => ({ value: item.code, label: `${item.flag} ${item.label}` }))]} />
            </div>
          ) : null}
          <div className="crm-leads-filter crm-leads-limit">
            <ArcSelect label="Na stránce" value={String(limit)} onChange={(value) => setLimit(Number(value))} options={['25', '50', '100', '200'].map((value) => ({ value, label: value }))} />
          </div>
          <button type="button" className="crm-leads-reset" onClick={clearFilters}>Vyčistit</button>
        </div>
      </ArcToolbar>

      <div className={`crm-lead-directory has-date${showKindFilter ? ' has-kind' : ''}`}>
        <div className="crm-directory-head"><span>Společnost</span><span>Kontakt</span>{showKindFilter ? <span>Typ</span> : null}<span>Hodnota / měsíc</span><span>Vlastník</span><span>Stav</span><span>Datum</span><span /></div>
        {error ? <div className="crm-directory-empty error">{error}</div>
          : loading ? <div className="crm-directory-empty">Načítám leady…</div>
          : rows.length === 0 ? <div className="crm-directory-empty">Zatím žádné leady.</div>
          : rows.map((c) => (
            <button key={c.id} type="button" className="crm-directory-row" onClick={() => onOpenDetail(String(c.id))}>
              <span className="crm-directory-company" data-label="Společnost"><strong>{c.company?.trim() || '—'}</strong>{showKindFilter
                ? <small>{c.website ? displayHost(c.website) : c.channels?.length ? `${c.channels[0]!.host}${c.channels.length > 1 ? ` +${c.channels.length - 1}` : ''}` : 'Bez webu'}{c.extendsPartnerId ? ' · rozšíření' : ''}</small>
                : <small className="crm-directory-brandline">{c.product ? <span>{c.product}</span> : null}{c.brand ? <b>{c.brand}</b> : null}{!c.product && !c.brand ? <span>{c.website ? displayHost(c.website) : 'Bez produktu'}</span> : null}</small>}{c.kind === 'inzerent' && c.services?.length ? <small className="crm-directory-services">{inzerentServiceLabels(c.services).map((label) => <i key={label}>{label}</i>)}</small> : null}</span>
              <span className="crm-directory-person"><i>{initials(c.firstName,c.lastName)}</i><span><strong>{`${c.firstName} ${c.lastName}`.trim() || 'Bez jména'}</strong><small>{c.email || c.phone || 'Kontakt není doplněný'}</small></span></span>
              {showKindFilter ? <span className="crm-directory-kind" data-label="Typ"><KindBadge kind={c.kind} />{leadFocusLabel(c, showKindFilter) ? <small>{leadFocusLabel(c, showKindFilter)}</small> : null}</span> : null}
              <span className="crm-directory-value" data-label="Hodnota">{c.expectedMonthlyEur ? money(c.expectedMonthlyEur, spaceSlug) : '—'}<small>měsíčně{c.expectedMonthlyEur && moneyAlt(c.expectedMonthlyEur, spaceSlug) ? ` · ${moneyAlt(c.expectedMonthlyEur, spaceSlug)}` : ''}</small></span>
              <span className={`crm-directory-owner${c.assignedToName ? '' : ' unassigned'}`} data-label="Vlastník">{c.assignedToName || 'Nepřiřazeno'}</span>
              <span className="crm-directory-status" data-label="Stav"><StatusBadge status={c.status} spaceSlug={spaceSlug}/>{showKindFilter ? <FlowMini lead={c} /> : null}</span>
              <span className="crm-directory-date" data-label="Datum">{fmtLeadDate(c.createdAt)}</span>
              <span className="crm-directory-arrow"><i data-feather="chevron-right" aria-hidden="true" /></span>
            </button>
          ))}
      </div>

      <nav className="crm-pagination" aria-label="Stránkování leadů">
        <div className="crm-pagination-state">
          <span>Stránka</span><strong>{Math.floor(offset / Math.max(1, limit)) + 1}</strong>
          {total > 0 ? ` · ${intFmt(total)} leadů` : ''}
        </div>
        <div className="crm-pagination-actions">
          <button type="button" disabled={offset === 0 || loading} onClick={() => setOffset(Math.max(0, offset - limit))} aria-label="Předchozí stránka"><i data-feather="chevron-left" aria-hidden="true" /></button>
          <button type="button" disabled={loading || rows.length < limit || offset + rows.length >= total} onClick={() => setOffset(offset + limit)} aria-label="Další stránka"><i data-feather="chevron-right" aria-hidden="true" /></button>
        </div>
      </nav>

      {view === 'new' ? (
        <NewLeadModal
          spaceSlug={spaceSlug}
          onClose={onCloseDetail}
          onCreated={() => { onCloseDetail(); setReloadKey((k) => k + 1); }}
        />
      ) : null}
    </ArcPage>
  );
}

function normalizeUrl(url: string): string {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}
function displayHost(url: string): string {
  return url.replace(/^https?:\/\//i, '').replace(/^www\./i, '').replace(/\/$/, '');
}

function NewLeadModal({ spaceSlug, onClose, onCreated }: { spaceSlug: string | null; onClose: () => void; onCreated: () => void }) {
  const focusOpts = focusOptionsForSlug(spaceSlug);
  const showProduct = showProductForSlug(spaceSlug);
  const showKind = spaceSlug === 'affilmaster';
  const [kind, setKind] = useState<LeadKind>('affiliate');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company, setCompany] = useState('');
  const [brand, setBrand] = useState('');
  const [channelsText, setChannelsText] = useState('');
  const [focus, setFocus] = useState('');
  const [product, setProduct] = useState('');
  const [module, setModule] = useState('');
  const [vocative, setVocative] = useState('');
  const [leadMode, setLeadMode] = useState<'new' | 'existing'>('new');
  const [clientQuery, setClientQuery] = useState('');
  const [clientHits, setClientHits] = useState<CrmClientHit[]>([]);
  const [clientPick, setClientPick] = useState<CrmClientHit | null>(null);
  const [lang, setLang] = useState('cs');
  const [langTouched, setLangTouched] = useState(false);
  const [platform, setPlatform] = useState('');
  const [affiliatePlatform, setAffiliatePlatform] = useState('');
  const [note, setNote] = useState('');
  const [platformSuggestions, setPlatformSuggestions] = useState<{ platforms: string[]; affiliatePlatforms: string[] }>({ platforms: [], affiliatePlatforms: [] });
  const [website, setWebsite] = useState('');
  const [ic, setIc] = useState('');
  const [dic, setDic] = useState('');
  const [inzType, setInzType] = useState('');
  const [services, setServices] = useState<string[]>([]);
  const [email, setEmail] = useState('');
  const [phonePrefix, setPhonePrefix] = useState('+420');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const isInzerent = showKind && kind === 'inzerent';
  const showPlatforms = isInzerent || !showKind;

  useEffect(() => {
    if (!showPlatforms) return;
    let cancelled = false;
    getCrmPlatformSuggestions().then((s) => { if (!cancelled) setPlatformSuggestions(s); }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [showPlatforms]);

  useEffect(() => {
    if (leadMode !== 'existing' || clientPick || clientQuery.trim().length < 2) { setClientHits([]); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      searchCrmClients(clientQuery).then((hits) => { if (!cancelled) setClientHits(hits); }).catch(() => undefined);
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [leadMode, clientQuery, clientPick]);

  function pickClient(hit: CrmClientHit) {
    setClientPick(hit);
    setClientHits([]);
    setCompany(hit.name);
    setIc(hit.ic ?? '');
    setDic(hit.vat ?? '');
    setWebsite(hit.website ?? '');
  }

  function clearClient() {
    setClientPick(null);
    setClientQuery('');
    setCompany('');
    setIc('');
    setDic('');
    setWebsite('');
  }

  const existing = leadMode === 'existing';
  const lockedCompany = existing && !!clientPick;

  function submit() {
    if (!firstName.trim() || !lastName.trim()) {
      setErr('Jméno i příjmení jsou povinné.');
      return;
    }
    if (existing && !clientPick) {
      setErr('Vyberte stávajícího klienta.');
      return;
    }
    if (existing && !brand.trim()) {
      setErr('U rozšíření stávajícího klienta zadejte název brandu.');
      return;
    }
    setSaving(true);
    setErr(null);
    createLead({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      ...(vocative.trim() ? { vocative: vocative.trim() } : {}),
      lang,
      kind: showKind ? kind : undefined,
      company: company.trim() || null,
      brand: isInzerent && !existing ? null : (brand.trim() || null),
      extendsPartnerId: existing && clientPick ? clientPick.id : undefined,
      channels: !showKind || isInzerent ? undefined : channelsText.split(/\r?\n/).map((s) => s.trim()).filter(Boolean),
      focus: isInzerent ? null : (focus || null),
      product: isInzerent ? (inzType || null) : showProduct ? (product || null) : null,
      module: showProduct ? module || null : undefined,
      platform: showPlatforms ? platform.trim() || null : undefined,
      affiliatePlatform: showPlatforms ? affiliatePlatform.trim() || null : undefined,
      note: note.trim() || null,
      services: isInzerent ? services : undefined,
      website: website.trim() || null,
      ic: ic.trim() || null,
      dic: dic.trim() || null,
      email: email.trim() || null,
      phone: phoneNumber.trim() ? `${phonePrefix} ${phoneNumber.trim()}` : null,
    })
      .then(() => onCreated())
      .catch((e: unknown) => setErr(e instanceof ApiError ? e.message : 'Uložení selhalo'))
      .finally(() => setSaving(false));
  }

  return (
    <div className="modal-backdrop-cf" onClick={onClose} style={backdropStyle}>
      <form
        className="crm-lead-modal crm-lead-create-modal"
        onClick={(event) => event.stopPropagation()}
        onSubmit={(event) => { event.preventDefault(); submit(); }}
      >
        <header className="crm-create-head">
          <div><span>CRM · nový kontakt</span><h2>Nový lead</h2><p>Založte příležitost a doplňte údaje pro první obchodní krok.</p></div>
          <button type="button" onClick={onClose} aria-label="Zavřít">×</button>
        </header>

        <div className="crm-create-body">
          <fieldset className="crm-create-kind">
            <legend>Klient</legend>
            <div>
              <button type="button" className={leadMode === 'new' ? 'active' : ''} onClick={() => { setLeadMode('new'); clearClient(); }}><span>Nový klient</span><small>Úplně nová firma</small></button>
              <button type="button" className={leadMode === 'existing' ? 'active' : ''} onClick={() => setLeadMode('existing')}><span>Stávající klient</span><small>Rozšíření o další business</small></button>
            </div>
            {existing ? (
              <div className="crm-create-client">
                {clientPick ? (
                  <div className="crm-create-client-pick"><strong>{clientPick.name}</strong>{clientPick.ic ? <small>IČ {clientPick.ic}</small> : null}<button type="button" onClick={clearClient} aria-label="Zrušit výběr klienta">×</button></div>
                ) : (
                  <>
                    <input value={clientQuery} onChange={(event) => setClientQuery(event.target.value)} placeholder="Hledat klienta podle názvu, IČ nebo webu…" autoFocus />
                    {clientHits.length > 0 ? (
                      <ul className="crm-create-client-hits">
                        {clientHits.map((hit) => <li key={hit.id}><button type="button" onClick={() => pickClient(hit)}><strong>{hit.name}</strong><small>{[hit.ic ? `IČ ${hit.ic}` : null, hit.website].filter(Boolean).join(' · ')}</small></button></li>)}
                      </ul>
                    ) : clientQuery.trim().length >= 2 ? <p className="crm-create-client-empty">Žádný klient neodpovídá.</p> : null}
                  </>
                )}
              </div>
            ) : null}
          </fieldset>
          {showKind ? (
            <fieldset className="crm-create-kind">
              <legend>Typ leadu</legend>
              <div>
                <button type="button" className={kind === 'affiliate' ? 'active' : ''} onClick={() => setKind('affiliate')}><span>Affiliate</span><small>Partner pro distribuci</small></button>
                <button type="button" className={kind === 'inzerent' ? 'active' : ''} onClick={() => setKind('inzerent')}><span>Inzerent</span><small>Klient a zadavatel</small></button>
              </div>
            </fieldset>
          ) : null}

          <section className="crm-create-section">
            <div className="crm-create-section-head"><span>01</span><div><h3>Kontakt</h3><p>Základní údaje osoby a spojení.</p></div></div>
            <div className="crm-create-grid">
              <label><span>Jméno *</span><input value={firstName} onChange={(event) => setFirstName(event.target.value)} autoComplete="given-name" /></label>
              <label><span>Příjmení *</span><input value={lastName} onChange={(event) => setLastName(event.target.value)} autoComplete="family-name" /></label>
              <label><span>Oslovení</span><input value={vocative} onChange={(event) => setVocative(event.target.value)} placeholder="doplní se z jména" /></label>
              <label><span>Jazyk komunikace</span><select value={lang} onChange={(event) => { setLang(event.target.value); setLangTouched(true); }}>{COMM_LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}</select></label>
              <label><span>E-mail</span><input value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" /></label>
              <label><span>Telefon</span><span className="crm-create-phone"><select value={phonePrefix} onChange={(event) => { const p = event.target.value; setPhonePrefix(p); if (!langTouched) setLang(p === '+420' ? 'cs' : p === '+421' ? 'sk' : 'en'); }} aria-label="Předvolba"><PhonePrefixOptions /></select><input value={phoneNumber} onChange={(event) => setPhoneNumber(event.target.value)} placeholder="Telefonní číslo" autoComplete="tel" /></span></label>
            </div>
          </section>

          <section className="crm-create-section">
            <div className="crm-create-section-head"><span>02</span><div><h3>Obchodní profil</h3><p>Kontext, podle kterého lead zařadíte.</p></div></div>
            <div className="crm-create-grid">
              <label><span>Společnost</span><input value={company} readOnly={lockedCompany} onChange={(event) => setCompany(event.target.value)} autoComplete="organization" /></label>
              <label><span>Web</span><input value={website} readOnly={lockedCompany} onChange={(event) => setWebsite(event.target.value)} placeholder="firma.cz" /></label>
              <label><span>IČ</span><input value={ic} readOnly={lockedCompany} onChange={(event) => setIc(event.target.value)} /></label>
              <label><span>DIČ</span><input value={dic} readOnly={lockedCompany} onChange={(event) => setDic(event.target.value)} /></label>
              {existing && isInzerent ? <label><span>Název brandu (rozšíření)</span><input value={brand} onChange={(event) => setBrand(event.target.value)} placeholder="např. Kasino Kartáč" /></label> : null}
              {isInzerent ? (
                <>
                  <label><span>Typ</span><select value={inzType} onChange={(event) => setInzType(event.target.value)}><option value="">Vyberte typ</option>{INZERENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}</select></label>
                  <div className="crm-create-grid-wide crm-create-services">
                    <span>Produkt</span>
                    <div>
                      {INZERENT_SERVICES.map((item) => (
                        <label key={item.value}>
                          <input
                            type="checkbox"
                            checked={services.includes(item.value)}
                            onChange={(event) => setServices((cur) => (event.target.checked ? [...cur, item.value] : cur.filter((v) => v !== item.value)))}
                          />
                          {item.label}
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <label><span>{existing ? 'Název brandu (rozšíření)' : 'Název brandu'}</span><input value={brand} onChange={(event) => setBrand(event.target.value)} placeholder={existing ? 'např. Kasino Kartáč' : undefined} /></label>
                  {showKind ? <label className="crm-create-grid-wide"><span>Propagační kanály (URL, každý na nový řádek, max. 5)</span><textarea rows={3} value={channelsText} onChange={(event) => setChannelsText(event.target.value)} placeholder={'https://www.instagram.com/…\nhttps://www.youtube.com/@…'} /></label> : null}
                  <label><span>Zaměření</span><select value={focus} onChange={(event) => setFocus(event.target.value)}><option value="">Vyberte zaměření</option>{focusOpts.map((option) => <option key={option} value={option}>{option}</option>)}</select></label>
                  {showProduct ? <label><span>Produkt</span><select value={product} onChange={(event) => setProduct(event.target.value)}><option value="">Vyberte produkt</option>{AP_PRODUCTS.map((option) => <option key={option} value={option}>{option}</option>)}</select></label> : null}
                  {showProduct ? <label><span>Modul</span><select value={module} onChange={(event) => setModule(event.target.value)}><option value="">Vyberte modul</option>{AP_MODULES.map((option) => <option key={option} value={option}>{option}</option>)}</select></label> : null}
                </>
              )}
              {showPlatforms ? (
                <>
                  <label><span>Kasino platforma</span><input value={platform} onChange={(event) => setPlatform(event.target.value)} placeholder="např. SoftSwiss, EveryMatrix, vlastní" list="crm-create-platform-options" /><datalist id="crm-create-platform-options">{platformSuggestions.platforms.map((p) => <option key={p} value={p} />)}</datalist></label>
                  <label><span>Affiliate platforma</span><input value={affiliatePlatform} onChange={(event) => setAffiliatePlatform(event.target.value)} placeholder="např. Post Affiliate Pro, Scaleo, žádná" list="crm-create-affiliate-platform-options" /><datalist id="crm-create-affiliate-platform-options">{platformSuggestions.affiliatePlatforms.map((p) => <option key={p} value={p} />)}</datalist></label>
                </>
              ) : null}
            </div>
          </section>

          <section className="crm-create-section">
            <div className="crm-create-section-head"><span>03</span><div><h3>Poznámka</h3><p>Interní kontext k leadu, zobrazí se v poznámkách.</p></div></div>
            <div className="crm-create-grid">
              <label className="crm-create-grid-wide"><span>Poznámka</span><textarea rows={3} value={note} onChange={(event) => setNote(event.target.value)} placeholder="Odkud lead přišel, co řeší, na co si dát pozor…" /></label>
            </div>
          </section>

          {err ? <div className="crm-create-error" role="alert">{err}</div> : null}
        </div>

        <footer className="crm-create-foot">
          <button type="button" className="crm-create-cancel" onClick={onClose} disabled={saving}>Zrušit</button>
          <button type="submit" className="crm-create-submit" disabled={saving}>{saving ? 'Ukládám…' : 'Vytvořit lead'}</button>
        </footer>
      </form>
    </div>
  );
}

const backdropStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15,23,42,.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
  zIndex: 1050,
};
